/**
 * FlightModeButton — header pill that lives to the LEFT of the bell.
 *
 * • Inactive: neutral plane icon, click opens the activation modal.
 * • Active:   highlighted icon + live `HH:MM:SS` countdown; click
 *              opens the "extend / cancel" modal.
 *
 * Polls `/api/flight-mode/status` every 15s and ticks the countdown
 * locally each second to avoid hammering the API.
 *
 * When a priority becomes available we fire a toast "Priority X
 * unlocked again" (per TZ §7.3).
 */
import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Plane, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

const PRIORITY_LABEL = { highest: 'Highest', high: 'High', medium: 'Medium', low: 'Low' };

function fmtHMS(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function FlightModeButton({ token }) {
  const [status, setStatus] = useState(null);
  // Remaining seconds — derived via a reducer fed by the 1-second
  // ticker AND by `fetchStatus` (when the modal mutates the session).
  // Using `useReducer` keeps `Date.now()` out of render — react-hooks
  // strict-mode forbids impure calls there.
  const [remaining, recomputeRemaining] = useReducer(
    (_prev, ends_at_iso) => {
      if (!ends_at_iso) return 0;
      return Math.max(0, Math.floor((new Date(ends_at_iso).getTime() - Date.now()) / 1000));
    },
    0,
  );
  const [open, setOpen] = useState(false);
  const lastAvail = useRef(null);
  const statusRef = useRef(null);   // mirrors `status` for the effect

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/flight-mode/status`, { headers });
      if (!r.ok) return;
      const j = await r.json();
      setStatus(j);
      statusRef.current = j;
      // Toast on each priority that just flipped to OPEN — per TZ §7.3.
      const cur = j.priority_availability || {};
      const prev = lastAvail.current;
      if (prev) {
        Object.entries(cur).forEach(([k, isOpen]) => {
          if (isOpen && prev[k] === false) {
            toast.success(`Priority "${PRIORITY_LABEL[k] || k}" is available again`);
          }
        });
      }
      lastAvail.current = cur;
    } catch (_) { /* network glitch — next tick will retry */ }
  };

  useEffect(() => { fetchStatus(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Local 1-second tick — bumps the counter so the derived `remaining`
  // value below recomputes once per second. Stops when no active session.
  useEffect(() => {
    if (!status?.active || !status.ends_at) {
      recomputeRemaining(null);
      return undefined;
    }
    recomputeRemaining(status.ends_at);                 // immediate
    const id = setInterval(() => recomputeRemaining(status.ends_at), 1000);
    return () => clearInterval(id);
  }, [status?.active, status?.ends_at]);

  // When the local countdown hits zero on an active session, re-fetch
  // so the UI flips back to inactive without waiting for the 15s poll.
  useEffect(() => {
    if (statusRef.current?.active && remaining <= 0) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  const active = !!status?.active;

  const tooltip = active
    ? `Flight Mode active — ${fmtHMS(remaining)} remaining`
    : 'Flight Mode — pause urgent priorities';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="flight-mode-btn"
        title={tooltip}
        className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 ${
          active
            ? 'bg-amber-400 text-slate-900 hover:bg-amber-300 shadow-sm'
            : 'bg-white/25 hover:bg-white/35 text-white'
        }`}
      >
        <Plane className={`w-4 h-4 ${active ? '' : 'opacity-90'}`} />
        {active && (
          <span data-testid="flight-mode-countdown" className="text-[11px] font-mono font-bold tabular-nums">
            {fmtHMS(remaining)}
          </span>
        )}
      </button>
      {open && (
        <FlightModeModal
          token={token}
          status={status}
          remaining={remaining}
          onClose={() => setOpen(false)}
          onChanged={(next) => {
            setStatus(next);
            lastAvail.current = next?.priority_availability || {};
          }}
        />
      )}
    </>
  );
}

function FlightModeModal({ token, status, remaining, onClose, onChanged }) {
  const [hours, setHours] = useState(1);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(null);   // 'activate' | 'extend' | 'cancel'
  const [err, setErr] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const isActive = !!status?.active;

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const submit = async (action) => {
    setErr(''); setBusy(action);
    try {
      const resolvedHours = custom ? Math.max(0.5, Number(custom)) : Number(hours);
      if (!resolvedHours || resolvedHours <= 0) throw new Error('Pick a duration > 0');
      let url = '';
      let body = {};
      if (action === 'activate') {
        url = `${API_URL}/api/flight-mode/activate`;
        body = { duration_hours: resolvedHours };
      } else if (action === 'extend') {
        url = `${API_URL}/api/flight-mode/extend`;
        body = { add_hours: resolvedHours };
      } else if (action === 'cancel') {
        url = `${API_URL}/api/flight-mode/cancel`;
        body = {};
      }
      const r = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      onChanged?.(d);
      if (action === 'cancel') onClose();
      if (action !== 'cancel') onClose();
    } catch (e) {
      setErr(e.message || `Failed to ${action}`);
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="flight-mode-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-bold flex items-center gap-2">
            <Plane className="w-4 h-4" /> {isActive ? 'Flight Mode active' : 'Activate Flight Mode'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/15 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {isActive && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
              <div className="font-bold text-amber-900">Time remaining</div>
              <div className="text-2xl font-mono font-bold text-slate-800 mt-1" data-testid="flight-mode-modal-remaining">{fmtHMS(remaining)}</div>
              {status?.next_open && (
                <div className="text-[11px] text-slate-600 mt-1">
                  Next priority opens: <b>{PRIORITY_LABEL[status.next_open.key] || status.next_open.key}</b> in {fmtHMS(status.next_open.in_seconds)}
                </div>
              )}
            </div>
          )}

          {!confirmCancel && (
            <>
              <p className="text-sm text-slate-700">
                {isActive ? 'Extend Flight Mode by:' : 'Activate Flight Mode for:'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => { setHours(h); setCustom(''); }}
                    data-testid={`flight-mode-h${h}`}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border-2 transition-all ${
                      !custom && hours === h
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {h} hour{h > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Custom (hours, 0.5 step)</label>
                <input
                  type="number" min={0.5} step={0.5}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="e.g. 4.5"
                  data-testid="flight-mode-custom"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </>
          )}

          {confirmCancel && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2 text-sm">
              <AlertTriangle className="w-5 h-5 text-rose-700 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-rose-900">Cancel Flight Mode immediately?</div>
                <p className="text-xs text-rose-800 mt-1">All priorities will be available again as soon as you confirm.</p>
              </div>
            </div>
          )}

          {err && (
            <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs">
              {err}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            {isActive && !confirmCancel && (
              <button
                type="button"
                onClick={() => setConfirmCancel(true)}
                data-testid="flight-mode-cancel-start"
                className="px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                Cancel Flight Mode
              </button>
            )}
            {confirmCancel && (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmCancel(false)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700"
                >
                  Keep active
                </button>
                <button
                  type="button"
                  onClick={() => submit('cancel')}
                  disabled={busy === 'cancel'}
                  data-testid="flight-mode-cancel-confirm"
                  className="px-3 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-rose-500 to-red-600 text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1.5"
                >
                  {busy === 'cancel' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Confirm cancel
                </button>
              </>
            )}
            {!confirmCancel && (
              <button
                type="button"
                onClick={() => submit(isActive ? 'extend' : 'activate')}
                disabled={!!busy}
                data-testid="flight-mode-submit"
                className="px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1.5"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isActive ? 'Extend' : 'Activate'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
